import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { 
  showRenewalNotification, 
  getNotificationPermission 
} from '@/utils/browserNotifications';
import { playNotificationSound } from '@/utils/notificationSound';

export interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  type: string;
  is_read: boolean;
  tool_id: string | null;
  created_at: string;
}

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Check if sound is enabled in user preferences
  const checkSoundEnabled = useCallback(async (): Promise<boolean> => {
    if (!user) return true; // Default to enabled
    try {
      const { data } = await supabase
        .from('notification_preferences')
        .select('sound_enabled')
        .eq('user_id', user.id)
        .single();
      return data?.sound_enabled ?? true;
    } catch {
      return true; // Default to enabled on error
    }
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      const typedData = (data || []) as Notification[];
      setNotifications(typedData);
      setUnreadCount(typedData.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, is_read: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;

      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [user]);

  // Subscribe to realtime notifications
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          setNotifications(prev => [newNotification, ...prev]);
          setUnreadCount(prev => prev + 1);

          // Play notification sound if enabled
          checkSoundEnabled().then((soundEnabled) => {
            if (soundEnabled) {
              if (newNotification.type === 'renewal_reminder') {
                playNotificationSound('renewal');
              } else {
                playNotificationSound('info');
              }
            }
          });

          // Show browser push notification for renewal reminders
          if (newNotification.type === 'renewal_reminder' && getNotificationPermission() === 'granted') {
            // Extract days from message (e.g., "renews in 3 days")
            const daysMatch = newNotification.message.match(/renews in (\d+) day/);
            const days = daysMatch ? parseInt(daysMatch[1]) : 7;
            
            // Extract cost from message (e.g., "$19.99/month")
            const costMatch = newNotification.message.match(/\$(\d+(?:\.\d+)?)/);
            const cost = costMatch ? parseFloat(costMatch[1]) : undefined;
            
            // Extract tool name from title or message
            const toolName = newNotification.title.replace('Subscription Renewal Reminder', '').trim() || 'Your tool';
            
            showRenewalNotification(toolName, days, cost);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, checkSoundEnabled]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch: fetchNotifications,
  };
}
